/**
 *  RaceCar
 *
 *  Created by Legman on 21/03/2017.
 *
 */

import Common from "../Common";
import RaceMover from "./RaceMover";

/**
 * @class
 * @extends RaceMover
 * @memberOf race
 */
export default class RaceCar extends RaceMover {
    /**
     * @param {number} id
     * @param {RaceCarView} view
     */
    constructor(id, view) {
        super(view);

        this.acceleration = 3.4;
        this.turnAccel = 10000;
        this.linearDamping = 0.994;
        this.turnDamping = 0.94;
        this.collisionWidth = 400;
        this.collisionLength = 50;

        /**
         * @member {signals.Signal}
         */
        this.signals.boost = new signals.Signal();

        /**
         * @type {number}
         */
        this.speedModifer = 1;

        /**
         * @member {number}
         */
        this.brakeDamping = 0.97;

        /**
         * @member {number}
         */
        this.minBrakeSpeed = 2;

        /**
         * @member {boolean}
         */
        this.usingPowerup = false;

        /**
         * @member {number}
         */
        this.offroadStart = 1240;

        /**
         * @member {number}
         */
        this.offroadCoef = 0.994;

        /**
         * @member {number}
         */
        this.roadLimit = 1420;

        /**
         * @member {number}
         * @protected
         */
        this._id = id;

        /**
         * @member {boolean}
         * @protected
         */
        this._boostActive = false;

        /**
         * @member {number}
         * @protected
         */
        this._boostMultiplier = 1.4;

        /**
         * @member {boolean}
         * @protected
         */
        this._jumping = false;

        /**
         * @member {number}
         * @protected
         */
        this._debuffDuration = 0;

        /**
         * @member {number}
         * @protected
         */
        this._debuffSlow = 0;

        /**
         * @member {number}
         * @protected
         */
        this._debuffSwerve = 0;

        /**
         * @member {number}
         * @protected
         */
        this._debuffTime = -1;

        /**
         * @type {number}
         * @protected
         */
        this._jumpOffset = 0;

        /**
         * @type {PIXI.particles.Emitter}
         * @private
         */
        this._dustEmitter = null;

        /**
         * @type {PIXI.particles.Emitter}
         * @private
         */
        this._impactLeftEmitter = null;

        /**
         * @type {PIXI.particles.Emitter}
         * @private
         */
        this._impactRightEmitter = null;

        /**
         * @type {number}
         * @private
         */
        this._impactTime = 0;
    }

    init() {
        this._view.startEngine();

        this._createDustEmitter();
        this._createImpactLeftEmitter();
        this._createImpactRightEmitter();
    }

    destroy() {
        this.signals.boost.dispose();

        super.destroy();
    }

    /**
     * @param {number} dt
     */
    update(dt) {
        if (this.moving) {
            let acceleration = this.acceleration * this.speedModifer;
            if (this._boostActive) {
                acceleration *= this._boostMultiplier;
            }
            this.velocity += acceleration * dt;
        }
        this.velocity *= this.linearDamping;
        this.distance += this.velocity * dt;

        let coef = Math.min(1, this.velocity / 12);
        this.turnVelocity += this.turnSpeed * coef * dt;
        this.turnVelocity *= this.turnDamping;
        this.offset.x += this.turnVelocity * dt;

        coef = this.velocity / 6;
        this.offset.x -= this.engine.getTrackCurvature(this.distance) * 6200 * coef * deltaTime;

        this.offset.x = Math.max(-this.roadLimit, Math.min(this.roadLimit, this.offset.x));
        if (Math.abs(this.offset.x) > this.offroadStart) {
            this._offroad = true;
            this.velocity *= this.offroadCoef;
            this._dustEmitter.emit = true;
        } else if (this._offroad) {
            this._offroad = false;
            this._dustEmitter.emit = false;
        }

        if (Math.abs(this.velocity) < 0.01) this.velocity = 0;
        if (Math.abs(this.turnVelocity) < 0.01) this.turnVelocity = 0;

        this.updateDebuff(dt);
        this.updateView(dt);
        this.updateLap(dt);

        this._dustEmitter.update(dt);
        this._impactLeftEmitter.update(dt);
        this._impactRightEmitter.update(dt);

        /*if (isNaN(this._view.position.x + this._view.position.y + this._view.scale.x + this._view.scale.y)) {
            alert("WE GOT A NAN IN THE HOUSE");
        }
        console.log(this._view.position, this._view.scale);
        */
    }

    /**
     * @param {number} dt
     */
    updateDebuff(dt) {
        if (this._debuffDuration <= 0) {
            this._debuffDuration = this._debuffSlow = this._debuffSwerve = 0;
            this._debuffTime = -1;
        } else {
            this._debuffDuration -= dt;
            this.turnVelocity += (Math.sin((++ this._debuffTime) * 0.012) * Math.random() * this.turnAccel * this._debuffSwerve) * dt;
            this.velocity *= this._debuffSlow;
        }
    }

    /**
     * @param {number} dt
     */
    updateView(dt) {
        const curvature = 2 * this.engine.getTrackCurvature(this.distance);
        const gradient = this.engine.getTrackGradient(this.distance);
        this._view.update(
            dt,
            this.distance,
            curvature * 160,
            gradient * 0.56,
            this.velocity,
            this.turnVelocity * 0.026,
            this._jumpOffset
        );
    }

    brake() {
        if (this.velocity > this.minBrakeSpeed) {
            this.velocity *= this.brakeDamping;
        }
    }

    /**
     * @param {boolean} [hit=true]
     */
    jump(hit = true) {
        if (this._jumping) return;
        this._jumping = true;

        const tl = new TimelineMax({onComplete: function() {
            this._jumping = false;
        }, onCompleteScope: this});

        tl.append(TweenMax.to(this, 0.3, {
            _jumpOffset: (hit ? 2 : 1),
            ease: Power2.easeOut
        }));
        tl.append(TweenMax.to(this, 0.2, {
            _jumpOffset: 0,
            ease: Power1.easeIn,
            onComplete: function() {
                (hit && this.view.visible) && Common.audio.playSound("sfx_car_land_00");
            },
            onCompleteScope: this
        }));
        tl.append(TweenMax.to(this, 0.1, {
            _jumpOffset: 0.12,
            ease: Power1.easeInOut
        }));
        tl.append(TweenMax.to(this, 0.08, {
            _jumpOffset: 0,
            ease: Power1.easeInOut
        }));

        this.view.visible && Common.audio.playSound("sfx_btn_nextcar_00");
    }

    /**
     * @param {number} [multiplier=1.4]
     */
    startBoost(multiplier = 1.4) {
        this._boostMultiplier = multiplier;
        this._boostActive = true;

        console.log("start boost", this);
    }

    stopBoost() {
        if (!this._boostActive) return;
        this._boostActive = false;

        console.log("stop boost", this);
    }

    /**
     * @param {number} [duration=1]
     * @param {number} [slow=0.996]
     * @param {number} [swerve=0.64]
     */
    applyDebuff(duration = 1, slow = 0.996, swerve = 0.64) {
        this._debuffDuration = Math.max(0, duration);
        this._debuffSlow = slow;
        this._debuffSwerve = swerve;

        if (this._debuffTime === -1) {
            this._debuffTime = Math.random() * Number.MAX_VALUE;
        }
    }

    /**
     * @param {boolean} [playSound=true]
     */
    takeHit(playSound = true) {
        this.velocity *= 0.6;
        this.jump();

        playSound && Common.audio.playSound([
            "sfx_car_hit_00",
            "sfx_car_hit_01",
            "sfx_car_hit_02",
            "sfx_car_hit_03",
            "sfx_car_hit_04",
            "sfx_car_hit_05",
            "sfx_car_hit_06",
            "sfx_car_hit_07",
            "sfx_car_hit_08",
            "sfx_car_hit_09",
            "sfx_car_hit_10"
        ]);
    }

    /**
     * @param {RaceCollision} collision
     * @param {RaceCar} car
     */
    onCollisionBegin(collision, car) {
        const velocity = Math.abs(this.turnVelocity + car.turnVelocity);
        if (window.performance.now() - this._impactTime > 4000 && velocity > 1000) {
            this._impactTime = window.performance.now();

            const dx = car.offset.x - this.offset.x;
            const sign = dx / Math.abs(dx);

            if (sign === -1) {
                this._impactLeftEmitter.emit = true;
            } else if (sign === 1) {
                this._impactRightEmitter.emit = true;
            }

            this._view.visible && Common.audio.playSound([
                "sfx_car_hit_00",
                "sfx_car_hit_01",
                "sfx_car_hit_02",
                "sfx_car_hit_03",
                "sfx_car_hit_04",
                "sfx_car_hit_05",
                "sfx_car_hit_06",
                "sfx_car_hit_07",
                "sfx_car_hit_08",
                "sfx_car_hit_09",
                "sfx_car_hit_10"
            ]);
        }
    }

    onCollisionEnd() {
    }

    _createDustEmitter() {
        const holder = new PIXI.Container();
        holder.y = 80;
        holder.scale = new PIXI.Point(2, 2);
        this._view.addChild(holder);

        const assets = Common.assets;
        const config = assets.getJSON("particle_emitter_offtrack_dust_00");
        this._dustEmitter = new PIXI.particles.Emitter(holder, [
            assets.getTexture("particles_car_dust_00"),
            assets.getTexture("particles_car_dust_01"),
            assets.getTexture("particles_car_dust_02"),
            assets.getTexture("particles_car_dust_03")
        ], config);
        this._dustEmitter.emit = false;
    }

    _createImpactLeftEmitter() {
        const holder = new PIXI.Container();
        holder.y = 80;
        holder.scale = new PIXI.Point(2, 2);
        this._view.addChild(holder);

        const assets = Common.assets;
        const config = assets.getJSON("particle_emitter_impact_left_00");
        this._impactLeftEmitter = new PIXI.particles.Emitter(holder, [
            assets.getTexture("particle_impact_star_orange"),
            assets.getTexture("particle_impact_star_white"),
            assets.getTexture("particle_impact_star_yellow")
        ], config);
        this._impactLeftEmitter.emit = false;
    }

    _createImpactRightEmitter() {
        const holder = new PIXI.Container();
        holder.y = 80;
        holder.scale = new PIXI.Point(2, 2);
        this._view.addChild(holder);

        const assets = Common.assets;
        const config = assets.getJSON("particle_emitter_impact_right_00");
        this._impactRightEmitter = new PIXI.particles.Emitter(holder, [
            assets.getTexture("particle_impact_star_orange"),
            assets.getTexture("particle_impact_star_white"),
            assets.getTexture("particle_impact_star_yellow")
        ], config);
        this._impactRightEmitter.emit = false;
    }

    /**
     * @returns {number}
     */
    get id() {
        return this._id;
    }

    /**
     * @returns {boolean}
     */
    get offroad() {
        return this._offroad;
    }

    /**
     * @returns {boolean}
     */
    get debuffed() {
        return this._debuffDuration > 0;
    }

    /**
     * @returns {boolean}
     */
    get isPlayer() {
        return this._id === Common.playerDriverId;
    }
}